import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import type ExcelJS from 'exceljs';
import { downloadWorkbook, styleTitle, styleHeader, styleDataRow, styleFooterRow, styleNoteRow, THIN_BORDER, XL_COLORS } from '../utils/excelHelper';
import { get } from '../services/api';
import { useSort, SortableTh } from '../components/TableTools';
import { hasFeature } from '../utils/featureFlags';

interface Vessel { id: string; vessel_name: string; }

interface Summary {
  user_name: string; vessel_name: string;
  total_work_days: number; total_work_minutes: number;
  total_overtime_minutes: number; total_break_minutes: number;
  average_daily_work_minutes: number; max_consecutive_work_days: number;
  work_type_breakdown: Record<string, number>;
}

interface AttRow {
  id: string;
  user_name: string; vessel_name: string; work_date: string;
  clock_in: string; clock_out: string;
  work_minutes: number; break_minutes: number; over_minutes: number;
  alcohol_value: number | null; status: string;
  work_type?: string | null;
}

/** 時間を指定して記録した作業のログ（work_activity_logs）。業務内容として同じ表に統合して出力する */
interface ActLog {
  id: string; work_session_id: string; name: string; emoji: string;
  started_at: string; ended_at: string; duration_minutes: number; note?: string | null;
}

/** 統合出力の注記（時間の二重計上を避けるための説明） */
const UNIFIED_NOTE =
  '※作業内容ごとに、かかった時間と回数（件数）をまとめています。'
  + '勤務時間の中で行った作業は勤務時間にも含まれるため、作業内容をまたいで時間を単純に合計しないでください。';

/** 対象月の日付範囲（YYYY-MM-DD） */
function monthRange(year: number, month: number): { from: string; to: string } {
  const mm = String(month).padStart(2, '0');
  const lastDay = new Date(year, month, 0).getDate();
  return { from: `${year}-${mm}-01`, to: `${year}-${mm}-${String(lastDay).padStart(2, '0')}` };
}

/** 作業ログだけを保持する入れ物セッションか（勤務0分で開始＝終了） */
function isActivityOnlyRow(r: AttRow): boolean {
  return Number(r.work_minutes ?? 0) === 0 && !!r.clock_in && r.clock_in === r.clock_out;
}

function fmtMin(m: number) {
  if (!m) return '-';
  return `${Math.floor(m / 60)}h${m % 60}m`;
}
function toCsvRow(cols: (string | number | null | undefined)[]): string {
  return cols.map(c => `"${String(c ?? '').replace(/"/g, '""')}"`).join(',');
}
function fmtTime(iso: string) {
  if (!iso) return '';
  try { return new Date(iso).toLocaleTimeString('ja-JP', { hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Tokyo', hour12: false }); }
  catch { return ''; }
}
function downloadBlob(csv: string, filename: string) {
  const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}
async function downloadExcel(
  headers: { label: string; width: number }[],
  rows: (string | number | null | undefined)[][],
  filename: string,
  titleText: string,
  totals?: (string | number | null)[],
  note?: string,
) {
  const { default: ExcelJS } = await import('exceljs');
  const wb = new ExcelJS.Workbook();
  wb.creator = 'CATMS';
  const ws = wb.addWorksheet('データ');
  ws.columns = headers.map(h => ({ width: h.width }));

  // Title row
  const titleRow = ws.addRow([titleText]);
  ws.mergeCells(1, 1, 1, headers.length);
  styleTitle(titleRow, headers.length);

  // 注記行（統合出力のときだけ。時間の数え方の注意を帳票に残す）
  if (note) {
    const noteRow = ws.addRow([note]);
    styleNoteRow(noteRow, headers.length);
    ws.mergeCells(noteRow.number, 1, noteRow.number, headers.length);
  }

  // Header row
  const headerRow = ws.addRow(headers.map(h => h.label));
  styleHeader(headerRow, headers.length);

  // Data rows
  rows.forEach((r, i) => {
    const dr = ws.addRow(r.map(v => v ?? ''));
    styleDataRow(dr, i, headers.length);
  });

  // 合計行
  if (totals && rows.length > 0) {
    const tr = ws.addRow(totals.map(v => v ?? ''));
    styleFooterRow(tr, headers.length);
  }

  // ヘッダー固定＋オートフィルターで閲覧しやすく
  ws.views = [{ state: 'frozen', ySplit: headerRow.number }];
  ws.autoFilter = {
    from: { row: headerRow.number, column: 1 },
    to:   { row: headerRow.number, column: headers.length },
  };

  await downloadWorkbook(wb, filename);
}

// 全データから作業種別一覧を収集（ソート済み）
function collectWorkTypes(rows: Summary[]): string[] {
  const set = new Set<string>();
  rows.forEach(r => Object.keys(r.work_type_breakdown ?? {}).forEach(t => set.add(t)));
  return [...set].sort();
}

const ReportsPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const now = new Date();
  const [year,     setYear]     = useState(() => Number(searchParams.get('year'))  || now.getFullYear());
  const [month,    setMonth]    = useState(() => Number(searchParams.get('month')) || now.getMonth() + 1);
  const [vesselId, setVesselId] = useState(() => searchParams.get('vessel') ?? '');
  const [vessels,  setVessels]  = useState<Vessel[]>([]);
  const [preview,  setPreview]  = useState<Summary[]>([]);
  const [loading,  setLoading]  = useState(false);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [format, setFormat] = useState<'csv' | 'xlsx'>('csv');
  // 統合表示: 業務の区分をなくし、作業内容ごとの「時間」と「回数」で帳票に出す会社かどうか
  const unified = hasFeature('unified_work_types');
  const [actLogs, setActLogs] = useState<ActLog[]>([]);
  // セッションID → 氏名・船名（作業ログに氏名が無いため勤怠側から引く）
  const [sessionOwner, setSessionOwner] = useState<Record<string, { user_name: string; vessel_name: string; work_date: string }>>({});
  // 「氏名｜船名」→ 作業名ごとの出退勤セッション数（＝回数）。月次集計の時間はサーバー集計を使うため回数だけを持つ
  const [workCountByUser, setWorkCountByUser] = useState<Record<string, Record<string, number>>>({});

  // プレビュー表のソート（固定列。合計行は全行対象のため順序に影響されない）
  const previewSort = useSort<Summary>(preview, {
    user_name:  r => r.user_name,
    vessel_name: r => r.vessel_name,
    total_work_days: r => r.total_work_days,
    total_work_minutes: r => r.total_work_minutes,
    total_overtime_minutes: r => r.total_overtime_minutes,
    max_consecutive_work_days: r => r.max_consecutive_work_days,
  }, 'user_name', 'asc');

  useEffect(() => {
    const p: Record<string, string> = { year: String(year), month: String(month) };
    if (vesselId) p.vessel = vesselId;
    setSearchParams(p, { replace: true });
  }, [year, month, vesselId, setSearchParams]);

  useEffect(() => {
    get<any>('/vessels').then(r => setVessels(r?.data ?? [])).catch(() => {});
  }, []);

  useEffect(() => {
    setLoadingPreview(true);
    const q: Record<string, string> = { target_year: String(year), target_month: String(month), per_page: '100' };
    if (vesselId) q['vessel_id'] = vesselId;
    get<any>('/monthly-summaries', q)
      .then(r => setPreview(r?.data ?? []))
      .catch(() => setPreview([]))
      .finally(() => setLoadingPreview(false));
  }, [year, month, vesselId]);

  // 統合表示のときだけ作業ログを読む（プレビューと帳票の両方で使う）
  useEffect(() => {
    if (!unified) { setActLogs([]); setSessionOwner({}); setWorkCountByUser({}); return; }
    const { from, to } = monthRange(year, month);
    const q: Record<string, string> = { date_from: from, date_to: to };
    if (vesselId) q['vessel_id'] = vesselId;
    Promise.all([
      get<any>('/activity-logs', q),
      get<any>('/attendance', q),
    ]).then(([actR, attR]) => {
      setActLogs((actR?.data ?? []) as ActLog[]);
      const owner: Record<string, { user_name: string; vessel_name: string; work_date: string }> = {};
      const counts: Record<string, Record<string, number>> = {};
      for (const r of (attR?.data ?? []) as AttRow[]) {
        owner[String(r.id)] = {
          user_name:   String(r.user_name ?? ''),
          vessel_name: String(r.vessel_name ?? ''),
          work_date:   String(r.work_date ?? '').slice(0, 10),
        };
        // 回数の数え方: 出退勤の記録1件＝1回。作業ログだけの入れ物セッションは実体が作業ログ側なので数えない
        if (isActivityOnlyRow(r)) continue;
        const wt = String(r.work_type ?? '').trim();
        if (!wt) continue;
        const key = `${r.user_name ?? ''}｜${r.vessel_name ?? ''}`;
        if (!counts[key]) counts[key] = {};
        counts[key]![wt] = (counts[key]![wt] ?? 0) + 1;
      }
      setSessionOwner(owner);
      setWorkCountByUser(counts);
    }).catch(() => { setActLogs([]); setSessionOwner({}); setWorkCountByUser({}); });
  }, [year, month, vesselId, unified]);

  /** 人（氏名×船名）ごとの作業ログ内訳。作業名ごとの時間と回数（1件＝1回） */
  const activityByUser = useMemo(() => {
    const map = new Map<string, { user: string; vessel: string; totals: Record<string, { min: number; count: number; emoji: string }> }>();
    for (const l of actLogs) {
      const meta = sessionOwner[l.work_session_id];
      if (!meta?.user_name) continue;
      const key = `${meta.user_name} ${meta.vessel_name}`;
      let entry = map.get(key);
      if (!entry) { entry = { user: meta.user_name, vessel: meta.vessel_name, totals: {} }; map.set(key, entry); }
      const cur = entry.totals[l.name] ?? { min: 0, count: 0, emoji: l.emoji ?? '' };
      entry.totals[l.name] = {
        min:   cur.min + (l.duration_minutes ?? 0),
        count: cur.count + 1,
        emoji: cur.emoji || (l.emoji ?? ''),
      };
    }
    return [...map.values()];
  }, [actLogs, sessionOwner]);

  /**
   * 統合表示の内訳: その人の「作業内容 / 合計時間 / 回数」。
   * 出退勤の業務（サーバー集計の work_type_breakdown ＋ セッション数）と
   * 時間を指定した作業ログを、同じ作業名なら合算する。
   */
  const mergedBreakdown = useCallback((
    user: string, vessel: string, bd: Record<string, number>,
  ): { name: string; min: number; count: number; emoji: string }[] => {
    const map: Record<string, { name: string; min: number; count: number; emoji: string }> = {};
    const put = (name: string, min: number, count: number, emoji: string) => {
      if (!map[name]) map[name] = { name, min: 0, count: 0, emoji };
      map[name]!.min   += min;
      map[name]!.count += count;
      if (!map[name]!.emoji) map[name]!.emoji = emoji;
    };
    const counts = workCountByUser[`${user}｜${vessel}`] ?? {};
    for (const [name, min] of Object.entries(bd)) put(name, min, counts[name] ?? 0, '');
    const act = activityByUser.find(a => a.user === user && a.vessel === vessel);
    for (const [name, v] of Object.entries(act?.totals ?? {})) put(name, v.min, v.count, v.emoji);
    return Object.values(map).sort((a, b) => b.min - a.min);
  }, [workCountByUser, activityByUser]);

  /** プレビュー全体の内訳（作業内容ごとの合計時間と回数） */
  const previewBreakdown = useMemo(() => {
    if (!unified) return [] as { name: string; min: number; count: number; emoji: string }[];
    const map: Record<string, { name: string; min: number; count: number; emoji: string }> = {};
    for (const r of preview) {
      for (const b of mergedBreakdown(r.user_name, r.vessel_name, r.work_type_breakdown ?? {})) {
        if (!map[b.name]) map[b.name] = { name: b.name, min: 0, count: 0, emoji: b.emoji };
        map[b.name]!.min   += b.min;
        map[b.name]!.count += b.count;
        if (!map[b.name]!.emoji) map[b.name]!.emoji = b.emoji;
      }
    }
    return Object.values(map).sort((a, b) => b.min - a.min);
  }, [preview, mergedBreakdown, unified]);

  const ext = format === 'xlsx' ? 'xlsx' : 'csv';
  const vesselName = vesselId ? (vessels.find(v => v.id === vesselId)?.vessel_name ?? '') : '';
  const vesselSuffix = vesselName ? `_${vesselName}` : '';
  const vesselTitle  = vesselName ? `（${vesselName}）` : '（全船舶）';

  async function exportData(
    headers: { label: string; width: number }[],
    rawRows: (string | number | null | undefined)[][],
    baseName: string,
    titleText: string,
    totals?: (string | number | null)[],
    note?: string,
  ) {
    if (format === 'xlsx') {
      try {
        await downloadExcel(headers, rawRows, `${baseName}.xlsx`, titleText, totals, note);
      } catch (e) {
        console.error('[Reports/excel]', e);
        alert('Excelの生成に失敗しました。ページを再読み込みしてから再度お試しください。');
      }
    } else {
      const headerLabels = headers.map(h => h.label);
      const body = rawRows.map(r => toCsvRow(r));
      downloadBlob([toCsvRow(headerLabels), ...body].join('\r\n'), `${baseName}.csv`);
    }
  }

  // A: 月次集計（作業別列を追加）
  const handleDownloadSummary = async () => {
    setLoading(true);
    try {
      const q: Record<string, string> = { target_year: String(year), target_month: String(month), per_page: '500' };
      if (vesselId) q['vessel_id'] = vesselId;
      const res = await get<any>('/monthly-summaries', q);
      const rows: Summary[] = res?.data ?? [];
      const workTypes = collectWorkTypes(rows);
      const baseHeaders: { label: string; width: number }[] = [
        { label: '氏名',           width: 18 },
        { label: '船名',           width: 18 },
        { label: '出勤日数',       width: 10 },
        { label: '総勤務時間(分)', width: 14 },
        { label: '残業時間(分)',   width: 13 },
        { label: '休憩時間(分)',   width: 13 },
        { label: '日平均(分)',     width: 12 },
        { label: '最大連勤(日)',   width: 12 },
      ];
      const workTypeHeaders: { label: string; width: number }[] = workTypes.map(t => ({ label: `${t}(分)`, width: 14 }));
      const headers = [...baseHeaders, ...workTypeHeaders];
      const rawRows = rows.map(r => [
        r.user_name, r.vessel_name,
        r.total_work_days, r.total_work_minutes, r.total_overtime_minutes,
        r.total_break_minutes, r.average_daily_work_minutes, r.max_consecutive_work_days,
        ...workTypes.map(t => r.work_type_breakdown?.[t] ?? 0),
      ]);
      const totals: (string | number | null)[] = [
        `合計（${rows.length}名）`, '',
        rows.reduce((s, r) => s + r.total_work_days, 0),
        rows.reduce((s, r) => s + r.total_work_minutes, 0),
        rows.reduce((s, r) => s + r.total_overtime_minutes, 0),
        rows.reduce((s, r) => s + r.total_break_minutes, 0),
        '', '',
        ...workTypes.map(t => rows.reduce((s, r) => s + (r.work_type_breakdown?.[t] ?? 0), 0)),
      ];
      const baseName = `月次集計_${year}年${month}月${vesselSuffix}`;
      await exportData(headers, rawRows, baseName, `月次集計　${year}年${month}月${vesselTitle}`, totals,
        unified ? '※作業内容ごとの時間と回数は「業務別集計」で出力できます。' : undefined);
    } catch { alert('生成に失敗しました'); }
    setLoading(false);
  };

  // B: 作業別集計（人×作業種別の1行）
  const handleDownloadWorkType = async () => {
    setLoading(true);
    try {
      const q: Record<string, string> = { target_year: String(year), target_month: String(month), per_page: '500' };
      if (vesselId) q['vessel_id'] = vesselId;
      const res = await get<any>('/monthly-summaries', q);
      const rows: Summary[] = res?.data ?? [];
      // 統合表示: 区分は出さず「作業内容ごとの時間と回数」を1行ずつ出す
      const headers: { label: string; width: number }[] = unified
        ? [
            { label: '氏名',     width: 18 },
            { label: '船名',     width: 18 },
            { label: '業務内容', width: 22 },
            { label: '時間(分)', width: 12 },
            { label: '時間',     width: 12 },
            { label: '回数',     width: 8  },
          ]
        : [
            { label: '氏名',     width: 18 },
            { label: '船名',     width: 18 },
            { label: '作業種別', width: 22 },
            { label: '時間(分)', width: 12 },
            { label: '時間',     width: 12 },
          ];
      const rawRows: (string | number)[][] = [];
      let normalMin = 0, totalCount = 0;
      rows.forEach(r => {
        const bd = r.work_type_breakdown ?? {};
        if (unified) {
          // 出退勤の業務と時間指定の作業を作業名で合算し、時間と回数を並べる
          const merged = mergedBreakdown(r.user_name, r.vessel_name, bd);
          if (merged.length === 0) {
            rawRows.push([r.user_name, r.vessel_name, '(未記録)', 0, '-', 0]);
          } else {
            for (const b of merged) {
              totalCount += b.count;
              rawRows.push([r.user_name, r.vessel_name, b.name, b.min, b.min > 0 ? fmtMin(b.min) : '-', b.count]);
            }
          }
          // 合計時間は勤務時間の合計のみ（勤務時間の中で行った作業を二重に数えないため）
          normalMin += Object.values(bd).reduce((s, m) => s + m, 0);
          return;
        }
        if (Object.keys(bd).length === 0) {
          rawRows.push([r.user_name, r.vessel_name, '(未記録)', 0, '-']);
        } else {
          Object.entries(bd)
            .sort((a, b) => b[1] - a[1])
            .forEach(([type, min]) => {
              normalMin += min;
              rawRows.push([r.user_name, r.vessel_name, type, min, min > 0 ? fmtMin(min) : '-']);
            });
        }
      });
      // 統合出力の合計は勤務時間の合計＋回数の合計（作業ごとの時間は勤務時間と重なる場合があるため単純合算しない）
      const totals: (string | number | null)[] = unified
        ? [`合計（勤務時間／${totalCount}回）`, '', '', normalMin, fmtMin(normalMin), totalCount]
        : [`合計（${rawRows.length}行）`, '', '', normalMin, fmtMin(normalMin)];
      const baseName = `${unified ? '業務別集計' : '作業別集計'}_${year}年${month}月${vesselSuffix}`;
      await exportData(headers, rawRows, baseName,
        `${unified ? '業務別集計' : '作業別集計'}　${year}年${month}月${vesselTitle}`, totals,
        unified ? UNIFIED_NOTE : undefined);
    } catch { alert('生成に失敗しました'); }
    setLoading(false);
  };

  // C: 勤怠明細
  const handleDownloadDetail = async () => {
    setLoading(true);
    try {
      const { from: dFrom, to: dTo } = monthRange(year, month);
      const q: Record<string, string> = { date_from: dFrom, date_to: dTo };
      if (vesselId) q['vessel_id'] = vesselId;
      const res = await get<any>('/attendance', q);
      const rows: AttRow[] = res?.data ?? [];
      // 統合表示: 区分は出さず、1行＝1件の業務として「業務内容」で見分ける（件数＝回数）
      const headers: { label: string; width: number }[] = unified
        ? [
            { label: '日付',             width: 13 },
            { label: '氏名',             width: 18 },
            { label: '船名',             width: 18 },
            { label: '業務内容',         width: 20 },
            { label: '開始',             width: 10 },
            { label: '終了',             width: 10 },
            { label: '時間(分)',         width: 10 },
            { label: '休憩(分)',         width: 10 },
            { label: '残業(分)',         width: 10 },
            { label: 'アルコール(mg/L)', width: 16 },
            { label: '状態',             width: 10 },
            { label: 'メモ',             width: 26 },
          ]
        : [
            { label: '日付',             width: 13 },
            { label: '氏名',             width: 18 },
            { label: '船名',             width: 18 },
            { label: '出勤',             width: 10 },
            { label: '退勤',             width: 10 },
            { label: '勤務(分)',         width: 10 },
            { label: '休憩(分)',         width: 10 },
            { label: '残業(分)',         width: 10 },
            { label: 'アルコール(mg/L)', width: 16 },
            { label: '状態',             width: 10 },
          ];
      // 作業ログはセッション単位で紐づける（帳票用に取り直す）
      const logsBySession: Record<string, ActLog[]> = {};
      if (unified) {
        const actRes = await get<any>('/activity-logs', q).catch(() => null);
        for (const l of ((actRes?.data ?? []) as ActLog[])) {
          const key = String(l.work_session_id);
          if (!logsBySession[key]) logsBySession[key] = [];
          logsBySession[key]!.push(l);
        }
      }
      const rawRows: (string | number)[][] = [];
      let detailCount = 0;
      for (const r of rows) {
        const logs = logsBySession[String(r.id)] ?? [];
        if (!unified) {
          rawRows.push([
            r.work_date, r.user_name, r.vessel_name,
            fmtTime(r.clock_in), fmtTime(r.clock_out),
            r.work_minutes, r.break_minutes, r.over_minutes,
            r.alcohol_value != null ? Number(r.alcohol_value).toFixed(3) : '',
            r.status === 'working' ? '勤務中' : '完了',
          ]);
          continue;
        }
        // 作業ログだけの入れ物セッションは出退勤の行を出さない（0時間の行が並ぶのを防ぐ）
        if (!(isActivityOnlyRow(r) && logs.length > 0)) {
          detailCount += 1;
          rawRows.push([
            r.work_date, r.user_name, r.vessel_name,
            r.work_type ?? '',
            fmtTime(r.clock_in), fmtTime(r.clock_out),
            r.work_minutes, r.break_minutes, r.over_minutes,
            r.alcohol_value != null ? Number(r.alcohol_value).toFixed(3) : '',
            r.status === 'working' ? '勤務中' : '完了', '',
          ]);
        }
        for (const l of logs) {
          detailCount += 1;
          rawRows.push([
            r.work_date, r.user_name, r.vessel_name,
            l.name,
            fmtTime(l.started_at), fmtTime(l.ended_at),
            l.duration_minutes ?? 0, '', '',
            '', '完了', l.note ?? '',
          ]);
        }
      }
      const workTotal  = rows.reduce((s, r) => s + (r.work_minutes ?? 0), 0);
      const breakTotal = rows.reduce((s, r) => s + (r.break_minutes ?? 0), 0);
      const overTotal  = rows.reduce((s, r) => s + (r.over_minutes ?? 0), 0);
      // 統合出力の合計時間は勤務時間の合計のみ（勤務時間の中で行った作業を二重に数えないため）
      const totals: (string | number | null)[] = unified
        ? [`合計（${detailCount}件／勤務時間）`, '', '', '', '', '',
           workTotal, breakTotal, overTotal, '', '', '']
        : [`合計（${rows.length}件）`, '', '', '', '',
           workTotal, breakTotal, overTotal, '', ''];
      const baseName = `勤怠明細_${year}年${month}月${vesselSuffix}`;
      await exportData(headers, rawRows, baseName, `勤怠明細　${year}年${month}月${vesselTitle}`, totals,
        unified ? UNIFIED_NOTE : undefined);
    } catch { alert('生成に失敗しました'); }
    setLoading(false);
  };

  // D: 執職日数集計（配乗時の役職ごとの乗船日数。当月＋年度累計）
  const handleDownloadPositionDays = async () => {
    setLoading(true);
    try {
      const mm = String(month).padStart(2, '0');
      const lastDay = new Date(year, month, 0).getDate();
      const from = `${year}-${mm}-01`;
      const to = `${year}-${mm}-${String(lastDay).padStart(2, '0')}`;
      // 年度起算日（乗下船表の設定と共通）から累計を計算する
      const st = await get<any>('/boarding-sheet/settings');
      const ys: string = st?.data?.boarding_year_start || '01-01';
      const fiscalFrom = `${year}-${ys}` <= from ? `${year}-${ys}` : `${year - 1}-${ys}`;
      const [mRes, fRes] = await Promise.all([
        get<any>('/boarding-sheet/position-days', { from, to }),
        get<any>('/boarding-sheet/position-days', { from: fiscalFrom, to }),
      ]);
      type PD = { user_id: string; user_name: string; employee_code: string; rank_position?: string | null; total_on: number; positions: Record<string, number>; duty_days?: Record<string, number> };
      const mRows: PD[] = mRes?.data ?? [];
      const fRows: PD[] = fRes?.data ?? [];
      const headers = [
        { label: '社員コード', width: 12 },
        { label: '氏名', width: 18 },
        { label: '辞令上の役職', width: 16 },
        { label: '執職（職務上執った役職）', width: 22 },
        { label: '当月執職日数', width: 14 },
        { label: 'うち役職と異なる執職(当月)', width: 22 },
        { label: '年度累計執職日数', width: 16 },
      ];
      // 当月に従事した執職を主軸に出力（年度累計は参考列）
      const rawRows: (string | number)[][] = [];
      for (const m2 of mRows) {
        const f = fRows.find(x => x.user_id === m2.user_id);
        for (const p of Object.keys(m2.positions)) {
          rawRows.push([m2.employee_code, m2.user_name, m2.rank_position ?? '', p,
                        m2.positions[p] ?? 0, m2.duty_days?.[p] ?? 0, f?.positions[p] ?? 0]);
        }
      }
      const baseName = `執職日数集計_${year}年${month}月`;
      await exportData(headers, rawRows, baseName, `執職日数集計　${year}年${month}月（全船舶）`, undefined,
        `※執職＝職務上執った役職（執職期間の登録＞乗船中の役職）。当月の日数が主軸で、年度累計は参考列です。乗船日・下船日はどちらも1日として数えます。年度累計は ${fiscalFrom} 起算（乗下船表の設定と共通）。`);
    } catch { alert('生成に失敗しました'); }
    setLoading(false);
  };

  const workTypes = collectWorkTypes(preview);

  return (
    <div className="page-wrap">
      <div className="top-bar">
        <h1 className="page-title">レポート生成</h1>
      </div>

      {/* 条件選択 */}
      <div className="card">
        <div className="card-h">レポート条件</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16, marginBottom: 20 }}>
          <div className="form-g" style={{ margin: 0 }}>
            <label className="form-l">年</label>
            <select className="form-i" value={year} onChange={e => setYear(Number(e.target.value))}>
              {[2024, 2025, 2026, 2027].map(y => <option key={y} value={y}>{y}年</option>)}
            </select>
          </div>
          <div className="form-g" style={{ margin: 0 }}>
            <label className="form-l">月</label>
            <select className="form-i" value={month} onChange={e => setMonth(Number(e.target.value))}>
              {Array.from({ length: 12 }, (_, i) => i + 1).map(m => <option key={m} value={m}>{m}月</option>)}
            </select>
          </div>
          <div className="form-g" style={{ margin: 0 }}>
            <label className="form-l">船舶</label>
            <select className="form-i" value={vesselId} onChange={e => setVesselId(e.target.value)}>
              <option value="">全船舶</option>
              {vessels.map(v => <option key={v.id} value={v.id}>{v.vessel_name}</option>)}
            </select>
            <p style={{ fontSize: 11, color: '#94a3b8', margin: '4px 0 0' }}>選択するとプレビュー・全レポートがその船舶で絞り込まれます</p>
          </div>
        </div>

        {/* 出力形式トグル */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
          <span style={{ fontSize: 13, color: '#6b7280', fontWeight: 500 }}>出力形式:</span>
          <div style={{ display: 'flex', borderRadius: 6, overflow: 'hidden', border: '1.5px solid #e2e8f0' }}>
            <button
              onClick={() => setFormat('csv')}
              style={{
                padding: '6px 18px', border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 600,
                background: format === 'csv' ? '#1d4ed8' : '#fff',
                color: format === 'csv' ? '#fff' : '#6b7280',
                transition: 'background 0.15s',
              }}>CSV</button>
            <button
              onClick={() => setFormat('xlsx')}
              style={{
                padding: '6px 18px', border: 'none', borderLeft: '1.5px solid #e2e8f0', cursor: 'pointer', fontSize: 13, fontWeight: 600,
                background: format === 'xlsx' ? '#1d4ed8' : '#fff',
                color: format === 'xlsx' ? '#fff' : '#6b7280',
                transition: 'background 0.15s',
              }}>Excel</button>
          </div>
          {format === 'xlsx' && (
            <span style={{ fontSize: 12, color: '#059669', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 4, padding: '2px 8px' }}>
              .xlsx 形式でダウンロード
            </span>
          )}
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 12 }}>
          <button className="btn-p" onClick={handleDownloadSummary} disabled={loading}
            style={{ padding: '12px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3 }}>
            <span style={{ fontWeight: 700 }}>月次集計{ext.toUpperCase()}</span>
            <span style={{ fontSize: 11, opacity: 0.8 }}>人別サマリー＋作業別列</span>
          </button>
          <button onClick={handleDownloadWorkType} disabled={loading}
            style={{
              padding: '12px', borderRadius: 8,
              border: `1.5px solid ${unified ? '#1d4ed8' : '#7c3aed'}`,
              background: unified ? '#eff6ff' : '#faf5ff', color: unified ? '#1d4ed8' : '#7c3aed', cursor: 'pointer',
              display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3,
            }}>
            <span style={{ fontWeight: 700 }}>{unified ? '業務別集計' : '作業別集計'}{ext.toUpperCase()}</span>
            <span style={{ fontSize: 11, opacity: 0.8 }}>{unified ? '人×業務内容ごとの時間と回数' : '人×作業種別ごとの時間'}</span>
          </button>
          <button className="btn-o" onClick={handleDownloadDetail} disabled={loading}
            style={{ padding: '12px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3 }}>
            <span style={{ fontWeight: 700 }}>勤怠明細{ext.toUpperCase()}</span>
            <span style={{ fontSize: 11, opacity: 0.8 }}>{unified ? '全業務データ（1件1行）' : '全打刻データ'}</span>
          </button>
          <button onClick={handleDownloadPositionDays} disabled={loading}
            style={{
              padding: '12px', borderRadius: 8, border: '1.5px solid #0e7490',
              background: '#ecfeff', color: '#0e7490', cursor: 'pointer',
              display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3,
            }}>
            <span style={{ fontWeight: 700 }}>執職日数集計{ext.toUpperCase()}</span>
            <span style={{ fontSize: 11, opacity: 0.8 }}>人×執職ごとの当月日数（年度累計も併記）</span>
          </button>
        </div>
        {loading && <div style={{ textAlign: 'center', marginTop: 12, color: '#64748b', fontSize: 13 }}>生成中...</div>}
      </div>

      {/* プレビュー */}
      <div className="card" style={{ marginTop: 16 }}>
        <div className="card-h">
          {year}年{month}月 月次集計プレビュー{vesselName ? `（${vesselName}）` : ''}
        </div>
        {loadingPreview ? (
          <div style={{ textAlign: 'center', padding: 24, color: '#94a3b8' }}>読み込み中...</div>
        ) : preview.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 24, color: '#94a3b8', fontSize: 13 }}>この月のデータがありません</div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ background: '#f8fafc', borderBottom: '2px solid #e2e8f0' }}>
                  <SortableTh label="氏名" k="user_name" sort={previewSort} />
                  <SortableTh label="船名" k="vessel_name" sort={previewSort} />
                  <SortableTh label="出勤" k="total_work_days" sort={previewSort} />
                  <SortableTh label="総勤務" k="total_work_minutes" sort={previewSort} />
                  <SortableTh label="残業" k="total_overtime_minutes" sort={previewSort} />
                  <SortableTh label="最大連勤" k="max_consecutive_work_days" sort={previewSort} />
                  {workTypes.map(t => (
                    <th key={t} style={{ padding: '10px 14px', textAlign: 'right', color: unified ? '#1d4ed8' : '#7c3aed', fontWeight: 600, whiteSpace: 'nowrap', fontSize: 12 }}>{t}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {previewSort.sorted.map((r, i) => (
                  <tr key={i} style={{ borderBottom: '1px solid #f1f5f9', background: i % 2 === 0 ? '#fff' : '#fafafa' }}>
                    <td style={{ padding: '10px 14px', fontWeight: 600 }}>{r.user_name}</td>
                    <td style={{ padding: '10px 14px', color: '#64748b' }}>{r.vessel_name}</td>
                    <td style={{ padding: '10px 14px' }}>{r.total_work_days}日</td>
                    <td style={{ padding: '10px 14px' }}>{fmtMin(r.total_work_minutes)}</td>
                    <td style={{ padding: '10px 14px', color: r.total_overtime_minutes > 0 ? '#dc2626' : '#94a3b8', fontWeight: r.total_overtime_minutes > 0 ? 700 : 400 }}>
                      {fmtMin(r.total_overtime_minutes)}
                    </td>
                    <td style={{ padding: '10px 14px', color: r.max_consecutive_work_days > 14 ? '#dc2626' : '#0f172a', fontWeight: r.max_consecutive_work_days > 14 ? 700 : 400 }}>
                      {r.max_consecutive_work_days}日
                    </td>
                    {workTypes.map(t => (
                      <td key={t} style={{ padding: '10px 14px', textAlign: 'right', color: unified ? '#1d4ed8' : '#7c3aed', fontSize: 12 }}>
                        {r.work_type_breakdown?.[t] ? fmtMin(r.work_type_breakdown[t] as number) : '-'}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr style={{ background: '#f0f9ff', borderTop: '2px solid #bfdbfe' }}>
                  <td style={{ padding: '10px 14px', fontWeight: 700, color: '#1d4ed8' }}>合計</td>
                  <td />
                  <td style={{ padding: '10px 14px', fontWeight: 700 }}>{preview.reduce((s, r) => s + r.total_work_days, 0)}日</td>
                  <td style={{ padding: '10px 14px', fontWeight: 700 }}>{fmtMin(preview.reduce((s, r) => s + r.total_work_minutes, 0))}</td>
                  <td style={{ padding: '10px 14px', fontWeight: 700, color: '#dc2626' }}>{fmtMin(preview.reduce((s, r) => s + r.total_overtime_minutes, 0))}</td>
                  <td />
                  {workTypes.map(t => (
                    <td key={t} style={{ padding: '10px 14px', textAlign: 'right', fontWeight: 700, color: unified ? '#1d4ed8' : '#7c3aed' }}>
                      {fmtMin(preview.reduce((s, r) => s + (r.work_type_breakdown?.[t] ?? 0), 0) as number)}
                    </td>
                  ))}
                </tr>
              </tfoot>
            </table>
            {/* 統合表示: 何をどれだけ作業したか（作業内容ごとの合計時間と回数） */}
            {unified && previewBreakdown.length > 0 && (
              <div style={{ marginTop: 14, background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10, padding: '10px 14px' }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: '#374151', marginBottom: 8 }}>業務内容ごとの時間と回数</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  {previewBreakdown.map(b => (
                    <span key={b.name} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, background: '#eff6ff', color: '#1d4ed8', border: '1px solid #bfdbfe', borderRadius: 20, padding: '4px 12px', fontSize: 12, fontWeight: 700, whiteSpace: 'nowrap' }}>
                      {b.emoji && <span>{b.emoji}</span>}
                      <span>{b.name}</span>
                      <span style={{ opacity: 0.85 }}>{fmtMin(b.min)}</span>
                      <span style={{ opacity: 0.75 }}>（{b.count}回）</span>
                    </span>
                  ))}
                </div>
                <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 8 }}>
                  回数は記録1件を1回として数えています。勤務時間の中で行った作業は総勤務にも含まれます。
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      <div className="card" style={{ marginTop: 16 }}>
        <div className="card-h">CSVの内容</div>
        <div style={{ fontSize: 13, color: '#6b7280', lineHeight: 1.8 }}>
          {unified ? (
            <>
              <p><b>月次集計</b>：人ごとの出勤日数・総勤務・残業・最大連勤に加え、業務内容ごとの時間を横に展開</p>
              <p><b>業務別集計</b>：人×業務内容を1行で展開。<b>どの作業にどれだけ時間がかかり、何回行ったか</b>（時間・回数）が分かります</p>
              <p><b>勤怠明細</b>：すべての業務を1件1行で出力（<b>「業務内容」列</b>で絞り込めば時間と件数を集計できます）</p>
              <p style={{ color: '#b45309' }}>※勤務時間の中で行った作業は勤務時間にも含まれます。業務内容をまたいで時間を単純に合計しないでください。</p>
              <p>Excel互換 UTF-8 BOM付き。</p>
            </>
          ) : (
            <>
              <p><b>月次集計</b>：人ごとの出勤日数・総勤務・残業・最大連勤に加え、作業種別ごとの時間を横に展開</p>
              <p><b>作業別集計</b>：人×作業種別を1行で展開（ピボット形式）</p>
              <p><b>勤怠明細</b>：全打刻データ（出勤・退勤・アルコール値）を1セッション1行で出力</p>
              <p>Excel互換 UTF-8 BOM付き。</p>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ReportsPage;
